import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { usePlaybackUrl, type VideoPlaybackInfo } from '../../../hooks/usePlaybackUrl';
import LibraryVideoPlayer from '../../propertyCard/LibraryVideoPlayer';
import type { CarouselItemContent } from '../../template/imageCarouselTypes';
import type { VideoAsset } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import {
  computeEmbedUrl,
  failedZoneMessage,
  libraryPlayerCardTitle,
  resolveVideoZoneView,
} from './resolveVideoZoneView';
import { VIDEO_ACCEPT, isVideoFile, type CardVideoUploadPhase } from './useCardVideoUpload';
import { useDragActive } from './useDragActive';
import VideoZoneEmbed from './VideoZoneEmbed';
import VideoZoneFailedView from './VideoZoneFailedView';

const frameSx = {
  position: 'relative' as const,
  bgcolor: '#111',
  height: 160,
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

function StatusOverlay({ label, progress }: { label: string; progress?: number }) {
  return (
    <Box sx={{ ...frameSx, flexDirection: 'column', gap: 1 }}>
      <CircularProgress size={24} sx={{ color: '#fff' }} />
      <Typography variant="caption" sx={{ color: '#fff' }}>
        {label}
        {typeof progress === 'number' ? ` ${Math.round(progress)}%` : ''}
      </Typography>
    </Box>
  );
}

function LibraryPlayer({
  externalId,
  getVideoPlaybackUrl,
  cardTitle,
}: {
  externalId: string;
  getVideoPlaybackUrl: (externalId: string) => Promise<VideoPlaybackInfo>;
  cardTitle?: string;
}) {
  const { url, title, description, loading, error } = usePlaybackUrl(
    externalId,
    getVideoPlaybackUrl,
  );
  if (error) {
    return (
      <Box sx={frameSx}>
        <Typography variant="caption" sx={{ color: '#fff', px: 2, textAlign: 'center' }}>
          Video unavailable
        </Typography>
      </Box>
    );
  }
  if (loading || !url) {
    return <StatusOverlay label="Loading video…" />;
  }
  return (
    <Box sx={frameSx}>
      <LibraryVideoPlayer
        url={url}
        title={cardTitle || title}
        description={description}
        videoStyle={{ objectFit: 'contain', backgroundColor: '#000' }}
      />
    </Box>
  );
}

function VideoDropHint({
  index,
  message,
  onVideoFile,
}: {
  index: number;
  message: string;
  onVideoFile: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <Box
        tabIndex={0}
        role="button"
        aria-label={`Slide ${index + 1} video — click or drag a file to upload`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const file = event.dataTransfer.files[0];
          if (file && isVideoFile(file)) onVideoFile(file);
        }}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          ...frameSx,
          bgcolor: '#f5f5f5',
          cursor: 'pointer',
          flexDirection: 'column',
          outline: 'none',
          '&:focus-visible': {
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <UploadIcon sx={{ fontSize: 36, mb: 0.5, opacity: 0.4 }} />
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
          {message}
        </Typography>
      </Box>
      <input
        type="file"
        accept={VIDEO_ACCEPT}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file && isVideoFile(file)) onVideoFile(file);
          event.target.value = '';
        }}
      />
    </>
  );
}

export default function ImageCarouselSlideVideoZone({
  index,
  item,
  phase,
  uploadProgress,
  asset,
  errorMessage,
  getVideoPlaybackUrl,
  onVideoFile,
  onOpenLibrary,
}: {
  index: number;
  item: CarouselItemContent;
  phase: CardVideoUploadPhase;
  uploadProgress: number;
  asset: VideoAsset | null;
  errorMessage: string | null;
  getVideoPlaybackUrl: (externalId: string) => Promise<{ url: string }>;
  onVideoFile: (file: File) => void;
  onOpenLibrary: () => void;
}) {
  const dragActive = useDragActive();
  const embedUrl = computeEmbedUrl(item);
  const view = resolveVideoZoneView(phase, asset, item, embedUrl);

  if (view === 'uploading') return <StatusOverlay label="Uploading…" progress={uploadProgress} />;
  if (view === 'transcoding') return <StatusOverlay label="Transcoding…" />;
  if (view === 'failed') {
    return (
      <VideoZoneFailedView
        index={index}
        message={failedZoneMessage(errorMessage, asset)}
        onVideoFile={onVideoFile}
        onOpenLibrary={onOpenLibrary}
      />
    );
  }
  if (view === 'library-ready') {
    return (
      <LibraryPlayer
        externalId={item.libraryVideoExternalId!}
        getVideoPlaybackUrl={getVideoPlaybackUrl}
        cardTitle={libraryPlayerCardTitle(item)}
      />
    );
  }
  if (view === 'library-loading') return <StatusOverlay label="Loading video…" />;
  if (view === 'embed')
    return <VideoZoneEmbed index={index} embedUrl={embedUrl} dragActive={dragActive} />;

  return (
    <VideoDropHint
      index={index}
      message="Drop a video, paste a URL below, or choose from the library"
      onVideoFile={onVideoFile}
    />
  );
}
